/**
 * SplitFlapRow.jsx
 *
 * A single row in the split-flap display representing one flight.
 * Handles row-level animations (enter/exit) and delegates to cells.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SplitFlapCell } from './SplitFlapCell.jsx';

// Column definitions - labelKey is the i18n key in ops namespace
const COLUMNS = [
  { key: 'flight', labelKey: 'colFlight', width: 6, className: 'splitflap-col-flight' },
  { key: 'route', labelKey: 'colRoute', width: 9, className: 'splitflap-col-route' },
  { key: 'status', labelKey: 'colStatus', width: 10, className: 'splitflap-col-status' },
  { key: 'weather', labelKey: 'colWeather', width: 8, className: 'splitflap-col-weather' },
  { key: 'delay', labelKey: 'colDelay', width: 5, className: 'splitflap-col-delay', align: 'right' },
  { key: 'eta', labelKey: 'colEta', width: 5, className: 'splitflap-col-eta' },
];

/**
 * Get status class for coloring based on flight state
 */
function getStatusClass(flight) {
  if (flight.status === 'cancelled' || flight.phase === 'cancelled') {
    return 'splitflap-status-cancelled';
  }
  if (flight.status === 'completed' || flight.phase === 'completed') {
    return 'splitflap-status-completed';
  }
  if (flight.diverted) {
    return 'splitflap-status-delayed';
  }
  if (flight.weatherHold) {
    return 'splitflap-status-weather';
  }
  if ((flight.totalDelayMinutes || 0) >= 30) {
    return 'splitflap-status-delayed';
  }
  if (flight.phase === 'boarding') {
    return 'splitflap-status-boarding';
  }
  if (['takeoff', 'climb', 'cruise', 'descent', 'approach', 'landing'].includes(flight.phase)) {
    return 'splitflap-status-inflight';
  }
  return 'splitflap-status-normal';
}

/**
 * Format status text from flight phase (with i18n support)
 */
function formatStatus(flight, t) {
  const phaseMap = {
    scheduled: t('ops.scheduled'),
    boarding: t('ops.boarding'),
    pushback: t('ops.pushback'),
    taxi_out: t('ops.taxiOut'),
    takeoff: t('ops.takeoff'),
    climb: t('ops.climbing'),
    cruise: t('ops.cruise'),
    descent: t('ops.descending'),
    approach: t('ops.approach'),
    landing: t('ops.landing'),
    taxi_in: t('ops.taxiIn'),
    arrived: t('ops.arrived'),
    completed: t('ops.completed'),
    cancelled: t('ops.cancelled'),
  };

  if (flight.weatherHold) return t('ops.wxHoldStatus');
  if (flight.diverted) return t('ops.diverted');

  return phaseMap[flight.phase] || flight.phase?.toUpperCase() || t('ops.unknown');
}

/**
 * Format weather text (with i18n support)
 */
function formatWeather(flight, airportWeather, t) {
  // Check destination weather for in-flight, origin weather for scheduled
  const airport = ['scheduled', 'boarding', 'pushback', 'taxi_out'].includes(flight.phase)
    ? flight.origin
    : flight.destination;

  const weather = airportWeather?.[airport]?.impact;

  if (!weather) return t('ops.weatherClear');

  const severityMap = {
    clear: t('ops.weatherClear'),
    advisory: t('ops.weatherAdvisory'),
    delay: t('ops.weatherDelays'),
    grounded: t('ops.weatherGrounded'),
  };

  return severityMap[weather.severity] || t('ops.weatherClear');
}

/**
 * Format delay text (with i18n support)
 */
function formatDelay(flight, t) {
  const delay = flight.totalDelayMinutes || 0;
  if (delay === 0) return t('ops.onTime');
  if (delay < 0) return t('ops.early');
  return `+${delay}M`;
}

/**
 * Format ETA
 */
function formatETA(flight) {
  if (!flight.scheduledDeparture) return '--:--';

  // Calculate ETA based on departure + flight time + delays
  const depTime = new Date(flight.scheduledDeparture);
  // Check multiple possible duration fields
  const flightHours = flight.flightDuration || flight.blockHours || flight.duration || 2;
  const delayMinutes = flight.totalDelayMinutes || 0;

  const eta = new Date(depTime.getTime() + (flightHours * 60 + delayMinutes) * 60000);
  // Use local time to match other displays (not UTC)
  const hours = eta.getHours();
  const minutes = eta.getMinutes();

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * SplitFlapRow Component
 */
export function SplitFlapRow({
  flight,
  airportWeather = {},
  isEntering = false,
  isExiting = false,
  animate = true,
  isArrivals = false,
}) {
  const { t } = useTranslation();
  const [rowClass, setRowClass] = useState('');

  useEffect(() => {
    if (isEntering) {
      setRowClass('entering');
      const timer = setTimeout(() => setRowClass(''), 400);
      return () => clearTimeout(timer);
    }
    if (isExiting) {
      setRowClass('exiting');
    }
  }, [isEntering, isExiting]);

  const statusClass = getStatusClass(flight);

  // Build cell values - for arrivals, show "FROM [origin]" format
  const routeDisplay = isArrivals
    ? `${flight.origin || '---'}-${flight.destination || '---'}`  // Show origin for arrivals
    : `${flight.origin || '---'}-${flight.destination || '---'}`;

  const cellValues = {
    flight: flight.flightNumber || '--',
    route: routeDisplay,
    status: formatStatus(flight, t),
    weather: formatWeather(flight, airportWeather, t),
    delay: formatDelay(flight, t),
    eta: formatETA(flight),
  };

  return (
    <div className={`splitflap-row ${rowClass} ${statusClass}`}>
      {COLUMNS.map((col) => (
        <div key={col.key} className={col.className}>
          <SplitFlapCell
            value={cellValues[col.key]}
            width={col.width}
            align={col.align || 'left'}
            animate={animate}
            statusClass={statusClass}
          />
        </div>
      ))}
    </div>
  );
}

// Export columns for header
export { COLUMNS };

export default SplitFlapRow;
